import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setSubmitStatus('success');
      setStatusMessage('感谢订阅！');
      return;
    }
    setSubmitStatus('loading');
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9u52rv8l97n3eas3hi0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* noop */ }
      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setSubmitStatus('success');
        setStatusMessage('感谢订阅！我们会在有新文章时通知你。');
        setEmail('');
      } else {
        const msg = (parsed as { meta?: { message?: string } }).meta?.message
          || (parsed as { message?: string }).message
          || '订阅失败，请稍后重试';
        setSubmitStatus('error');
        setStatusMessage(msg);
      }
    } catch {
      setSubmitStatus('error');
      setStatusMessage('网络错误，请检查连接后重试');
    }
  };

  return (
    <footer className="bg-background-900 text-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <h3 className="font-heading text-lg font-semibold text-background-50 mb-3">
              {siteConfig.name}
            </h3>
            <p className="text-sm text-background-300 leading-relaxed">
              科学认识身体，悦纳真实自我。
              专注于女性性健康科普，用温暖、专业的方式陪伴每一位女性探索自我。
            </p>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-background-50 mb-4">内容分类</h4>
            <ul className="space-y-2.5">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-sm text-background-300 hover:text-background-50 transition-colors"
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-background-50 mb-4">快捷链接</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/" className="text-sm text-background-300 hover:text-background-50 transition-colors">
                  首页
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-background-300 hover:text-background-50 transition-colors">
                  关于我们
                </Link>
              </li>
              <li>
                <Link to="/category/xingjiankang-kepu" className="text-sm text-background-300 hover:text-background-50 transition-colors">
                  性健康科普
                </Link>
              </li>
              <li>
                <Link to="/category/ziwei-jiqiao" className="text-sm text-background-300 hover:text-background-50 transition-colors">
                  自慰技巧
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-background-50 mb-4">邮件订阅</h4>
            <p className="text-sm text-background-300 mb-3 leading-relaxed">
              订阅我们的邮件，第一时间获取最新科普文章。
            </p>
            <form
              data-readdy-form
              onSubmit={handleSubscribe}
              className="flex flex-col gap-2"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="visually-hidden-field"
              />
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="输入你的邮箱"
                  required
                  className="flex-1 px-3 py-2 text-sm rounded-md bg-background-800 border border-background-700 text-background-50 placeholder:text-background-500 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitStatus === 'loading'}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer"
                >
                  {submitStatus === 'loading' ? '提交中...' : '订阅'}
                </button>
              </div>
              {submitStatus !== 'idle' && (
                <p className={`text-xs ${submitStatus === 'success' ? 'text-secondary-400' : 'text-red-400'}`}>
                  {statusMessage}
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-background-700/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-background-400">
            &copy; {siteConfig.foundingYear} - {new Date().getFullYear()} {siteConfig.name}. 本站内容仅供科普参考，不能替代专业医疗建议。
          </p>
          <p className="text-xs text-background-500">
            <Link to="/about" className="hover:text-background-300 transition-colors">联系我们</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}